import { Box, Typography, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { motion } from 'framer-motion';

export type UrgencyLevel = 'low' | 'medium' | 'high' | 'critical';

export const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

export const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const urgencyStyles = {
  low: {
    // Safe - calm green/teal
    background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
    borderColor: '#00897b',
    textColor: '#00695c',
    iconColor: '#00897b',
    accentGradient: 'linear-gradient(90deg, transparent, rgba(0, 137, 123, 0.08), transparent)',
  },
  medium: {
    // Caution - soft blue
    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
    borderColor: '#1976d2',
    textColor: '#0d47a1',
    iconColor: '#1976d2',
    accentGradient: 'linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.1), transparent)',
  },
  high: {
    // Warning - orange/amber
    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
    borderColor: '#f57c00',
    textColor: '#e65100',
    iconColor: '#f57c00',
    accentGradient: 'linear-gradient(90deg, transparent, rgba(245, 124, 0, 0.1), transparent)',
  },
  critical: {
    // Urgent - red
    background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
    borderColor: '#e53935',
    textColor: '#c62828',
    iconColor: '#e53935',
    accentGradient: 'linear-gradient(90deg, transparent, rgba(229, 57, 53, 0.12), transparent)',
  },
};

export const BannerContainer = styled(motion.div)<{ urgency: UrgencyLevel }>(({ urgency }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '10px 16px',
  background: urgencyStyles[urgency].background,
  borderBottom: `2px solid ${urgencyStyles[urgency].borderColor}`,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: urgencyStyles[urgency].accentGradient,
    backgroundSize: '200% 100%',
    animation: `${shimmer} 3s ease-in-out infinite`,
    pointerEvents: 'none',
  },
}));

export const TopRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
  '@media (max-width: 720px)': {
    flexWrap: 'wrap',
    rowGap: '8px',
  },
});

export const ContentWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
  minWidth: 0,
  zIndex: 1,
  flexWrap: 'wrap',
  rowGap: '8px',
});

export const IconWrapper = styled(Box)<{ urgency: UrgencyLevel }>(({ urgency }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  flexShrink: 0,
  animation: urgency === 'critical' ? `${pulse} 1.5s ease-in-out infinite` : 'none',
}));

export const TextContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flexShrink: 0,
});

export const DaysText = styled(Typography)<{ urgency: UrgencyLevel }>(({ urgency }) => ({
  fontWeight: 700,
  fontSize: '0.875rem',
  color: urgencyStyles[urgency].textColor,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
}));

export const SubText = styled(Typography)<{ urgency: UrgencyLevel }>(({ urgency }) => ({
  fontSize: '0.75rem',
  color: urgencyStyles[urgency].textColor,
  opacity: 0.85,
  lineHeight: 1.3,
}));

export const ButtonRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  zIndex: 1,
});

export const ActionsWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  zIndex: 1,
  flexShrink: 0,
});

export const PurchaseButton = styled('button')<{ urgency: UrgencyLevel }>(({ urgency }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 18px',
  borderRadius: '24px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.8rem',
  fontWeight: 700,
  letterSpacing: '0.3px',
  color: '#fff',
  // Vibrant gradient that pops against any urgency background
  background: urgency === 'critical' || urgency === 'high'
    ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' // Purple for urgency
    : 'linear-gradient(135deg, #059669 0%, #10b981 100%)', // Emerald green for safe
  boxShadow: urgency === 'critical' || urgency === 'high'
    ? '0 4px 14px rgba(124, 58, 237, 0.4), 0 2px 6px rgba(0,0,0,0.1)'
    : '0 4px 14px rgba(5, 150, 105, 0.35), 0 2px 6px rgba(0,0,0,0.1)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  flexShrink: 0,
  zIndex: 1,
  '&:hover': {
    transform: 'translateY(-2px) scale(1.02)',
    boxShadow: urgency === 'critical' || urgency === 'high'
      ? '0 6px 20px rgba(124, 58, 237, 0.5), 0 3px 8px rgba(0,0,0,0.15)'
      : '0 6px 20px rgba(5, 150, 105, 0.45), 0 3px 8px rgba(0,0,0,0.15)',
  },
  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },
}));

export const CloseButton = styled(IconButton)<{ urgency: UrgencyLevel }>(({ urgency }) => ({
  padding: '4px',
  color: urgencyStyles[urgency].textColor,
  opacity: 0.6,
  zIndex: 1,
  flexShrink: 0,
  '&:hover': {
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
}));
